import { z } from 'zod';

/**
 * Maps Zod validation errors to a flat field errors object.
 * Uses the first error message per field.
 */
export function zodErrorsToFieldErrors(
  error: z.ZodError,
): Record<string, string> {
  const flattened = z.flattenError(error);
  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(flattened.fieldErrors)) {
    if (Array.isArray(messages) && messages.length > 0 && messages[0]) {
      result[field] = messages[0];
    }
  }
  return result;
}

/**
 * Extracts field-level validation errors from API error response.
 * Backend format: { validation: { first_name: "...", email: "Email already exists" } }
 * Also maps top-level message to email field when it's clearly an email error.
 */
export function apiValidationToFieldErrors(
  data:
    | {
        validation?: Record<string, string | string[]>;
        message?: string;
      }
    | undefined,
): Record<string, string> | null {
  if (!data) return null;

  const result: Record<string, string> = {};

  if (data.validation && typeof data.validation === 'object') {
    for (const [field, message] of Object.entries(data.validation)) {
      if (Array.isArray(message) && message.length > 0 && message[0]) {
        result[field] = message[0];
      } else if (typeof message === 'string' && message.trim()) {
        result[field] = message;
      }
    }
  }

  if (
    Object.keys(result).length === 0 &&
    typeof data.message === 'string' &&
    data.message.trim()
  ) {
    const msg = data.message.toLowerCase();
    if (
      msg.includes('email') &&
      (msg.includes('already') || msg.includes('exists'))
    ) {
      result.email = data.message;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}
